import React, { useEffect, useState } from 'react';

// Types
import { SlackChannelType } from 'Shared/types/SlackChannel.type';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Hooks
import { useWorkspaceAgendaAi } from 'Shared/utils/hooks/use-workspace-agenda-ai';
import { useToggle } from 'Shared/utils/hooks';

// Components
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import WpAlert from 'Wpds/components/alert/WpAlert';
import SlackWorkspaceSelect from 'Shared/components/SlackWorkspaceSelect/SlackWorkspaceSelect';
import OAuthButton from '../OAuthButton/OAuthButton';

// Styles
import './WorkspaceAgendaAiSettingsModal.scss';

// Hooks
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

type Props = {
  isOpen: boolean;
  toggleIsOpen: () => void;
  toggleSettingsModal: Function;
};

const WorkspaceAgendaAiSettingsModal = ({ isOpen, toggleIsOpen, toggleSettingsModal }: Props) => {
  const {
    slackAiOrganizationDataSourceId,
    isFetching,
    availableSlackchannels,
    selectedChannels,
    handleCreateOrUpdateWorkspaceAiSetting,
  } = useWorkspaceAgendaAi();

  const { id: workspaceId } = useWorkspace();

  const [selected, setSelected] = useState<SlackChannelType[]>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isSaving, toggleIsSaving] = useToggle(false);

  const hasPrivate = selected?.find(channel => channel.is_private) || null;

  const OAuthRedictUrl = `${PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId)}?show=agenda_ai`;

  const handleOnChange = (channels: SlackChannelType[]) => {
    setHasError(false);
    setSelected(channels);
  };

  const handleSave = () => {
    if (selected === null || selected.length === 0) {
      setHasError(true);
      return;
    }
    toggleIsSaving();
    handleCreateOrUpdateWorkspaceAiSetting({ channels: selected.map(({ id }) => id) }).then(response => {
      if (response.status === 200) {
        toggleIsSaving();
        WpToastManager.success('Channels saved');
        toggleSettingsModal();
        toggleIsOpen();
      }
    });
  };

  useEffect(() => {
    if (!isFetching && selected === null && availableSlackchannels.length > 0) {
      setSelected(selectedChannels);
    }
  }, [isFetching, selected, selectedChannels, availableSlackchannels]);

  const baseClassName = 'WorkspaceAgendaAiSettingsModal';

  return (
    <WpModal isOpen={isOpen} onToggle={toggleIsOpen} dismissOnBackdrop={false} size="sm">
      <WpModalHeader>AgendaAI Settings</WpModalHeader>
      <WpModalBody>
        <div className={baseClassName}>
          {!slackAiOrganizationDataSourceId ? (
            <div className={`${baseClassName}__connect`}>
              <p>Connect Slack to use AgendaAI</p>
              <div>
                <OAuthButton
                  oauthProvider="slack"
                  text="Connect Slack"
                  block={false}
                  size="md"
                  color="primary"
                  variant="primary"
                  iconType="icon"
                  redirectUrl={OAuthRedictUrl}
                />
              </div>
            </div>
          ) : (
            <>
              {isFetching || selected === null ? (
                <>Fetching Channels...</>
              ) : (
                <SlackWorkspaceSelect
                  options={availableSlackchannels}
                  selected={selected}
                  onChange={handleOnChange}
                  errorMsg={hasError ? 'Please select at least 1 channel' : null}
                  label="Select the Slack channels to use in this workspace"
                  isDisabled={isSaving}
                />
              )}
            </>
          )}
          {hasPrivate && (
            <div className={`${baseClassName}__private-note`}>
              <WpAlert type="note">
                Please note, by selecting a private channel, the PI Perform Slack bot will automatically be added to
                that channel.
              </WpAlert>
            </div>
          )}
        </div>
      </WpModalBody>
      <WpModalFooter>
        {slackAiOrganizationDataSourceId ? (
          <WpButtonGroup>
            <WpButton variant="tertiary" onClick={toggleIsOpen}>
              Cancel
            </WpButton>
            <WpButton onClick={handleSave} loading={isSaving}>
              Save
            </WpButton>
          </WpButtonGroup>
        ) : (
          <WpButton onClick={toggleIsOpen} variant="tertiary">
            Close
          </WpButton>
        )}
      </WpModalFooter>
    </WpModal>
  );
};

export default WorkspaceAgendaAiSettingsModal;
